/**
 * Auto-Gift Manager
 * Automatically adds/removes gift products based on cart contents
 */

class AutoGiftManager {
  constructor(config) {
    this.enabled = config.enabled;
    this.triggerProductId = config.triggerProductId;
    this.giftProductId = config.giftProductId;
    this.giftVariantId = config.giftVariantId;
    this.quantityMode = config.quantityMode; // 'fixed' or 'match'
    
    if (!this.enabled || !this.triggerProductId || !this.giftProductId) {
      return;
    }
    
    this.processing = false;
    this.userRemovedGift = false; // Track if user manually removed the gift
    this.lastCartState = null; // Track last cart state
    this.init();
  }
  
  init() {
    // Listen for the theme's cart:update event
    document.addEventListener('cart:update', (e) => {
      this.handleCartUpdateEvent(e);
    });
    
    // Initial check on page load
    setTimeout(() => {
      this.checkAndUpdateCart();
    }, 1000);
  }
  
  async handleCartUpdateEvent(event) {
    // Ignore events from ourselves to prevent loops
    if (event.detail?.data?.source === 'auto-gift-manager') {
      return;
    }
    
    // Process immediately
    this.checkAndUpdateCart();
  }
  
  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#cart-drawer'
      },
      {
        id: 'cart-icon-bubble',
        selector: '.cart-count-bubble'
      }
    ];
  }
  
  renderCartSections(responseData) {
    const sectionsToRender = this.getSectionsToRender();
    
    sectionsToRender.forEach((section) => {
      const sectionElement = document.querySelector(section.selector);
      if (sectionElement && responseData.sections && responseData.sections[section.id]) {
        const elementToReplace = sectionElement.querySelector(`[data-section-id="${section.id}"]`) || sectionElement;
        
        // Create a temporary container to parse the HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = responseData.sections[section.id];
        const newContent = tempDiv.querySelector(`[data-section-id="${section.id}"]`) || tempDiv.firstElementChild;
        
        if (newContent && elementToReplace) {
          elementToReplace.replaceWith(newContent);
        }
      }
    });
  }
  
  async handleProductAdded(detail) {
    if (this.processing) return;
    
    // Check if the added product is the trigger product
    const addedProductId = detail?.product_id || detail?.productId;
    
    if (addedProductId && String(addedProductId) === String(this.triggerProductId)) {
      await this.addGiftProduct(detail.quantity || 1);
    }
  }
  
  async handleCartUpdate() {
    if (this.processing) return;
    await this.checkAndUpdateCart();
  }
  
  async checkAndUpdateCart() {
    if (this.processing) return;
    this.processing = true;
    
    try {
      const cart = await this.getCart();
      const triggerItem = this.findItemByProductId(cart.items, this.triggerProductId);
      const giftItem = this.findGiftItem(cart.items);
      
      // Detect if user manually removed the gift
      if (this.lastCartState) {
        const hadGift = this.findGiftItem(this.lastCartState.items);
        const hadTrigger = this.findItemByProductId(this.lastCartState.items, this.triggerProductId);
        
        // If gift was removed but trigger is still there, user manually removed it
        if (hadGift && !giftItem && hadTrigger && triggerItem) {
          this.userRemovedGift = true;
          this.lastCartState = JSON.parse(JSON.stringify(cart));
          this.processing = false;
          return;
        }
        
        // Reset flag only if trigger was completely removed and re-added (cart was empty)
        if (!hadTrigger && !hadGift && triggerItem) {
          this.userRemovedGift = false;
        }
      }
      
      if (triggerItem) {
        // Trigger product is in cart
        const requiredGiftQty = this.quantityMode === 'match' ? triggerItem.quantity : 1;
        
        if (!giftItem && !this.userRemovedGift) {
          await this.addGiftProduct(requiredGiftQty);
        } else if (giftItem && giftItem.quantity !== requiredGiftQty && !this.userRemovedGift) {
          await this.updateGiftQuantity(giftItem.key, requiredGiftQty);
        }
      } else {
        // Trigger product not in cart
        if (giftItem) {
          await this.removeGiftProduct(giftItem.key);
        }
        this.userRemovedGift = false;
      }
      
      // Store current cart state for next comparison
      const finalCart = await this.getCart();
      this.lastCartState = JSON.parse(JSON.stringify(finalCart));
    } catch (error) {
      // Silent fail
    } finally {
      this.processing = false;
    }
  }
  
  findItemByProductId(items, productId) {
    return items.find(item => String(item.product_id) === String(productId));
  }

  findGiftItem(items) {
    return items.find((item) => {
      const properties = item.properties || {};
      return (
        String(item.product_id) === String(this.giftProductId) &&
        properties._auto_gift === 'true' &&
        String(properties._auto_gift_for) === String(this.triggerProductId)
      );
    });
  }
  
  async getCart() {
    const response = await fetch('/cart.js');
    return await response.json();
  }
  
  async addGiftProduct(quantity = 1) {
    try {
      const sections = this.getSectionsToRender().map((section) => section.id);
      const formData = {
        items: [{
          id: this.giftVariantId,
          quantity: quantity,
          properties: {
            _free_gift: 'true',
            _auto_gift: 'true',
            _auto_gift_for: String(this.triggerProductId)
          }
        }],
        sections: sections.join(','),
        sections_url: window.location.pathname
      };
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        const responseData = await response.json();
        
        // Render the updated cart sections
        this.renderCartSections(responseData);
        
        // Dispatch cart update event
        const event = new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: responseData,
            sourceId: 'auto-gift-manager',
            data: {
              source: 'auto-gift-manager',
              itemCount: responseData.item_count,
              giftProductId: this.giftProductId,
              sections: responseData.sections
            }
          }
        });
        document.dispatchEvent(event);
      }
    } catch (error) {
      // Silent fail
    }
  }
  
  async updateGiftQuantity(lineKey, quantity) {
    try {
      const sections = this.getSectionsToRender().map((section) => section.id);
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: quantity,
          sections: sections.join(',')
        })
      });
      
      if (response.ok) {
        const responseData = await response.json();
        
        // Render the updated cart sections
        this.renderCartSections(responseData);
        
        // Dispatch cart update event
        const event = new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: responseData,
            sourceId: 'auto-gift-manager',
            data: {
              source: 'auto-gift-manager',
              itemCount: responseData.item_count,
              sections: responseData.sections
            }
          }
        });
        document.dispatchEvent(event);
      }
    } catch (error) {
      // Silent fail
    }
  }
  
  async removeGiftProduct(lineKey) {
    try {
      const sections = this.getSectionsToRender().map((section) => section.id);
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
          sections: sections.join(',')
        })
      });
      
      if (response.ok) {
        const responseData = await response.json();
        
        // Render the updated cart sections
        this.renderCartSections(responseData);
        
        // Dispatch cart update event
        const event = new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: responseData,
            sourceId: 'auto-gift-manager',
            data: {
              source: 'auto-gift-manager',
              itemCount: responseData.item_count,
              sections: responseData.sections
            }
          }
        });
        document.dispatchEvent(event);
      }
    } catch (error) {
      // Silent fail
    }
  }
  
  triggerCartRefresh() {
    // Trigger cart refresh event for other components
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    
    // Also trigger the standard cart updated event
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAutoGift);
} else {
  initAutoGift();
}

function initAutoGift() {
  // Configuration will be injected from Liquid
  if (window.autoGiftConfig) {
    window.autoGiftManager = new AutoGiftManager(window.autoGiftConfig);
  }
}
